import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Users,
  MapPin,
  Star,
  ArrowRight,
  Heart
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const RockledgePage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Regular cleaning for Rockledge families. Weekly, bi-weekly, or monthly service with the same trusted team every visit."
    },
    {
      title: "Deep Cleaning",
      description: "Comprehensive cleaning for established Rockledge homes. Perfect for seasonal refreshes or when you need a reset."
    },
    {
      title: "Move-In / Move-Out Cleaning",
      description: "Inspection-ready cleaning for Rockledge home transitions, helping renters, homeowners, and landlords alike."
    },
    {
      title: "Post-Construction Cleaning",
      description: "Final cleanup after renovations or construction projects, removing dust and debris to reveal your finished space."
    }
  ];

  const perfectFor = [
    "Families with kids in Rockledge schools",
    "Homeowners in established neighborhoods",
    "Busy professionals juggling work and family",
    "Properties near US-1 and Fiske Blvd corridors",
    "Residents in River Groves and surrounding areas",
    "Landlords managing rental properties in Rockledge"
  ];

  const faqs = [
    {
      question: "Do you serve all of Rockledge?",
      answer: "Yes. We serve all Rockledge neighborhoods—from River Groves to areas near the Rockledge Country Club, US-1 corridor, Fiske Boulevard, and throughout the community. We're familiar with the area and its residential character."
    },
    {
      question: "Are you pet-friendly?",
      answer: "Absolutely. Many of our Rockledge clients have dogs, cats, and other pets. We'll work around their routines and treat your furry family members with care."
    },
    {
      question: "What makes Pierce Premier different from other cleaning companies serving Rockledge?",
      answer: "We're locally owned by Bob and Heather, not a franchise. You'll have the same cleaners every visit, and Heather's 15+ years of experience means we deliver professional results consistently. We're easy to reach and accountable to you."
    },
    {
      question: "Can you work around our family schedule?",
      answer: "Yes. We understand families have busy schedules—school, work, activities. We're flexible and work with your availability to make cleaning fit into your life, not the other way around."
    },
    {
      question: "Do you offer one-time cleanings or only recurring service?",
      answer: "Both. While many of our Rockledge clients become recurring customers, we're happy to handle one-time deep cleans, move-in/move-out cleaning, or post-construction cleanup as well."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Rockledge, FL | Pierce Premier",
      description: "Trusted house cleaning in Rockledge, FL. Established community specialists. Same team every visit, 15+ years experience, fully insured, pet-friendly. Call 321-408-1347.",
      url: "/areas/rockledge"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Rockledge" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Rockledge",
      description: "Professional house cleaning services in Rockledge, FL including recurring cleaning, deep cleaning, move-in/move-out, and post-construction cleanup."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Rockledge, FL | Pierce Premier"
        description="Trusted house cleaning in Rockledge, FL. Established families. Same team every visit, 15+ years experience, fully insured, pet-friendly. Call 321-408-1347."
        canonical="/areas/rockledge"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-rose-600" />
                  <Badge className="bg-rose-600 text-white">Established Community</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Rockledge, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Rockledge is an established community where families have put down roots—good schools, residential neighborhoods, and that sense of stability people look for when they choose where to live. Pierce Premier serves this community with cleaning services built for busy families who value consistency and reliability.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you're in River Groves, near the Rockledge Country Club, or anywhere in this family-oriented area, we bring the same commitment to quality and professionalism you expect from local service providers.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-rose-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-rose-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-rose-600 text-rose-600 px-8 py-4 rounded-lg font-semibold hover:bg-rose-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=800&q=80" 
                  alt="House cleaning services for families in Rockledge, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Rockledge Families Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Built for Busy Families</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Rockledge families are juggling work, school, activities, and everything else. We're reliable, flexible, and easy to work with—cleaning fits into your schedule and takes one thing off your plate.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team Every Time</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. You'll have the same cleaners every visit, so they learn your home, your preferences, and become familiar faces you can trust.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Pet-Friendly Service</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We love pets! Many of our Rockledge clients have dogs, cats, and other animals. We'll work around their routines and treat them with the care they deserve.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience to every Rockledge home. We know what quality cleaning looks like, and we deliver it consistently.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Rockledge
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Rockledge has that established community feel—families who've been here for years, good schools, neighborhoods where people actually know each other. It's not flashy, but it's solid, and we appreciate serving residents who've chosen this area for those reasons.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  When you're raising a family or building a life here, having reliable service providers matters. We're glad to be part of keeping your home comfortable and clean so you can focus on everything else.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Services for Rockledge Homes
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-rose-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Perfect For Rockledge
              </h2>
              <p className="text-gray-600">Pierce Premier is trusted by:</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {perfectFor.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-rose-100">
                  <CheckCircle2 className="w-5 h-5 text-rose-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Rockledge Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "River Groves",
                "Rockledge Country Club Area",
                "US-1 Corridor",
                "Fiske Blvd Area",
                "Barton Avenue",
                "Orange Avenue"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-rose-50 rounded-lg border border-rose-100">
                  <CheckCircle2 className="w-5 h-5 text-rose-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Codes:</span> 32955, 32956
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Cocoa", link: "#" },
                { name: "Merritt Island", link: "#" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Cocoa Beach", link: "#" },
                { name: "Palm Bay", link: "/areas/palm-bay" }
              ].map((city, idx) => (
                city.link === "#" ? (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <span className="text-gray-600">{city.name}</span>
                  </div>
                ) : (
                  <Link key={idx} to={city.link} className="p-4 bg-rose-50 rounded-lg border-2 border-rose-200 hover:border-rose-400 transition-colors text-center group">
                    <span className="text-rose-700 font-semibold group-hover:text-rose-900">{city.name}</span>
                    <ArrowRight className="w-4 h-4 inline-block ml-1 text-rose-600" />
                  </Link>
                )
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Rockledge Residents
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <CTASection 
          title="Ready to Simplify Your Life?"
          description="Let Pierce Premier handle the cleaning while you focus on your family. Reliable service, consistent team, and quality you can count on."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default RockledgePage;